import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { OperatorUsersService, type InviteOperatorInput, type OperatorUser } from './OperatorUsersService';

@injectable()
export class OperatorUsersViewModel extends BaseViewModel {
    users: OperatorUser[] | null = null;
    loading = true;
    error: string | null = null;
    busy = new Set<string>();

    constructor(private readonly service: OperatorUsersService) {
        super();
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            this.users = await this.service.list();
        } catch (e) {
            this.users = null;
            this.error = e instanceof Error ? e.message : 'Failed to load operators';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    isBusy(id: string): boolean {
        return this.busy.has(id);
    }

    async invite(input: InviteOperatorInput): Promise<void> {
        await this.service.invite(input);
        await this.load();
    }

    async setEnabled(id: string, enabled: boolean): Promise<void> {
        await this.run(id, () => this.service.setEnabled(id, enabled));
    }

    async remove(id: string): Promise<void> {
        await this.run(id, () => this.service.remove(id));
    }

    private async run(id: string, action: () => Promise<void>): Promise<void> {
        this.busy.add(id);
        this.notifyChange();
        try {
            await action();
            await this.load();
        } finally {
            this.busy.delete(id);
            this.notifyChange();
        }
    }
}
